import { useState, useRef } from "react";
import { Send, Mic, Square, Plus, Image, Camera, X, MapPin } from "lucide-react";
import axios from "axios";
import LocationPicker from "@/components/LocationPicker";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ChatInput({ onSendMessage, onSendVoice, onSendImage, onSendLocation, isLoading, sessionId }) {
  const [text, setText] = useState("");
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  const [showAttachMenu, setShowAttachMenu] = useState(false);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const handleSend = () => {
    if (imageFile) {
      if (onSendImage) onSendImage(imageFile, text.trim());
      setImageFile(null);
      setImagePreview(null);
      setText("");
      return;
    }
    if (!text.trim() || isLoading) return;
    onSendMessage(text.trim());
    setText("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      alert("Please select an image file (JPEG, PNG, WebP)");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      alert("Image too large. Please select an image under 10MB.");
      return;
    }
    setImageFile(file);
    setShowAttachMenu(false);
    const reader = new FileReader();
    reader.onload = (ev) => setImagePreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const clearImage = () => {
    setImageFile(null);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (cameraInputRef.current) cameraInputRef.current.value = "";
  };

  const handleLocationConfirm = async (location) => {
    setShowLocationPicker(false);
    if (!location?.address) return;
    if (onSendLocation && location.lat != null && location.lng != null) {
      onSendLocation(location.lat, location.lng);
      return;
    }
    if (sessionId && location.lat != null && location.lng != null) {
      try {
        await axios.post(`${API}/chat/location`, {
          session_id: sessionId, latitude: location.lat, longitude: location.lng,
        });
      } catch (_) {}
    }
    onSendMessage(`My location: ${location.address}`);
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "audio/mp4",
      });
      chunksRef.current = [];
      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        stream.getTracks().forEach((t) => t.stop());
        if (blob.size > 0) onSendVoice(blob);
      };
      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);
      timerRef.current = setInterval(() => setRecordingTime((prev) => prev + 1), 1000);
    } catch (err) {
      console.error("Microphone access denied:", err);
      alert("Microphone access is needed for voice messages. Please allow microphone access in your browser settings.");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      clearInterval(timerRef.current);
      setRecordingTime(0);
    }
  };

  const formatRecordTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  const hasContent = text.trim() || imageFile;

  return (
    <div className="chat-input-area px-3 py-2 md:px-6 md:py-3 shrink-0" data-testid="input-area">
      {/* Image preview */}
      {imagePreview && (
        <div className="max-w-3xl mx-auto mb-2" data-testid="image-preview-container">
          <div className="relative inline-block bg-white rounded-lg shadow-sm border p-2">
            <img src={imagePreview} alt="Upload preview" className="max-h-32 rounded object-contain" data-testid="image-preview" />
            <button onClick={clearImage} className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center shadow-md hover:bg-red-600" data-testid="clear-image-btn" aria-label="Remove image">
              <X className="w-3 h-3" />
            </button>
          </div>
        </div>
      )}

      <div className="flex items-center gap-2 md:gap-3 max-w-3xl mx-auto">
        {isRecording ? (
          <div className="flex-1 flex items-center gap-3 bg-white rounded-full px-4 py-3 shadow-sm">
            <div className="w-3 h-3 rounded-full bg-red-500 recording-pulse" />
            <span className="text-base text-red-600 font-medium flex-1">Recording... {formatRecordTime(recordingTime)}</span>
            <button onClick={stopRecording} className="w-10 h-10 rounded-full bg-red-500 flex items-center justify-center hover:bg-red-600 transition-colors" aria-label="Stop recording" data-testid="stop-recording-btn">
              <Square className="w-4 h-4 text-white fill-white" />
            </button>
          </div>
        ) : (
          <>
            {/* Attach button (+) — opens gallery/camera/location picker */}
            <div className="relative">
              <button
                onClick={() => setShowAttachMenu(!showAttachMenu)}
                disabled={isLoading}
                className={`w-10 h-10 md:w-11 md:h-11 rounded-full flex items-center justify-center transition-all disabled:opacity-50 shrink-0 ${
                  showAttachMenu ? "bg-[#2D2766] text-white rotate-45" : "text-gray-500 hover:text-[#2D2766] hover:bg-white"
                }`}
                aria-label="Attach photo or share location"
                data-testid="attach-btn"
              >
                <Plus className="w-6 h-6" />
              </button>

              {/* Attach popup */}
              {showAttachMenu && (
                <div className="absolute bottom-14 left-0 bg-white rounded-xl shadow-xl border p-2 flex gap-1 z-20 animate-in fade-in slide-in-from-bottom-2 duration-150" data-testid="attach-menu">
                  <button
                    onClick={() => { fileInputRef.current?.click(); }}
                    className="flex flex-col items-center gap-1 p-3 rounded-lg hover:bg-purple-50 transition-colors min-w-[68px]"
                    aria-label="Upload photo from gallery"
                    data-testid="attach-gallery-btn"
                  >
                    <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                      <Image className="w-5 h-5 text-purple-600" />
                    </div>
                    <span className="text-[10px] text-gray-600 font-medium">Gallery</span>
                  </button>
                  <button
                    onClick={() => { cameraInputRef.current?.click(); }}
                    className="flex flex-col items-center gap-1 p-3 rounded-lg hover:bg-green-50 transition-colors min-w-[68px]"
                    aria-label="Take photo with camera"
                    data-testid="attach-camera-btn"
                  >
                    <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                      <Camera className="w-5 h-5 text-green-600" />
                    </div>
                    <span className="text-[10px] text-gray-600 font-medium">Camera</span>
                  </button>
                  <button
                    onClick={() => { setShowAttachMenu(false); setShowLocationPicker(true); }}
                    className="flex flex-col items-center gap-1 p-3 rounded-lg hover:bg-blue-50 transition-colors min-w-[68px]"
                    aria-label="Share your location"
                    data-testid="attach-location-btn"
                  >
                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                      <MapPin className="w-5 h-5 text-blue-600" />
                    </div>
                    <span className="text-[10px] text-gray-600 font-medium">Location</span>
                  </button>
                </div>
              )}
            </div>

            <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={handleImageSelect} className="hidden" data-testid="image-file-input" />
            <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" onChange={handleImageSelect} className="hidden" data-testid="camera-file-input" />

            <div className="flex-1 relative">
              <input
                type="text"
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={handleKeyDown}
                onFocus={() => setShowAttachMenu(false)}
                placeholder={imageFile ? "Add delivery address or caption..." : isLoading ? "Please wait..." : "Type a message..."}
                disabled={isLoading}
                className="w-full bg-white rounded-full px-5 py-3 md:py-3.5 text-base md:text-lg border-none shadow-sm focus:outline-none focus:ring-2 focus:ring-[#2D2766] placeholder-gray-400 disabled:opacity-60"
                style={{ minHeight: "48px" }}
                data-testid="chat-input"
                aria-label="Type your message"
              />
            </div>

            {hasContent ? (
              <button onClick={handleSend} disabled={isLoading}
                className="w-12 h-12 md:w-14 md:h-14 rounded-full flex items-center justify-center shadow-lg transition-all active:scale-95 disabled:opacity-50 shrink-0"
                style={{ backgroundColor: "#E8B830" }} aria-label="Send message" data-testid="send-btn">
                <Send className="w-5 h-5 md:w-6 md:h-6 text-white" />
              </button>
            ) : (
              <button onClick={startRecording} disabled={isLoading}
                className="w-12 h-12 md:w-14 md:h-14 rounded-full flex items-center justify-center shadow-lg transition-all active:scale-95 disabled:opacity-50 shrink-0"
                style={{ backgroundColor: "#E8B830" }} aria-label="Record voice message" data-testid="voice-btn">
                <Mic className="w-5 h-5 md:w-6 md:h-6 text-white" />
              </button>
            )}
          </>
        )}
      </div>

      {/* Close attach menu when clicking outside */}
      {showAttachMenu && (
        <div className="fixed inset-0 z-10" onClick={() => setShowAttachMenu(false)} />
      )}

      {/* Location Picker Modal */}
      {showLocationPicker && (
        <LocationPicker sessionId={sessionId} onConfirm={handleLocationConfirm} onClose={() => setShowLocationPicker(false)} />
      )}
    </div>
  );
}
